function localDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatPublicationEffectiveDate(value) {
  if (!value) return "";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function getPublicationDisplayName(publication) {
  if (!publication) return "Published model";
  if (
    publication.source_type === "legacy" ||
    String(publication.name || "")
      .toLowerCase()
      .includes("legacy unversioned baseline")
  ) {
    return "Original model";
  }
  return publication.name || "Published model";
}

export function getScenarioDisplayName(scenario) {
  const name = String(scenario?.name || "").trim();
  if (!name) return "Sandbox scenario";

  const restoredName = name.match(
    /^Restored\s+v\d+\s*(?:[·:\-—]\s*)?(.*)$/i
  );
  if (restoredName) {
    return restoredName[1] ? `Copy of ${restoredName[1]}` : "Copied model";
  }

  return name;
}

export function getScenarioDisplayDescription(scenario) {
  const description = String(scenario?.description || "").trim();
  if (!description) return "";
  if (/^Restored from published version\s+\d+\.?$/i.test(description)) {
    return "Copied from a published model.";
  }
  return description;
}

export function canPublishScenario(viewMode, scenario) {
  // An unchanged snapshot can still be intentionally republished with a new
  // effective date, so publication is not limited to content-hash changes.
  return Boolean(viewMode === "saved" && scenario?.uuid);
}

export function getPublicationStatus(
  publication,
  activePublicationUuid,
  today = localDateKey()
) {
  if (!publication) {
    return { label: "Loading publication status…", shortLabel: "Loading", tone: "muted" };
  }

  const effectiveDate = publication.effective_date;
  const formattedDate = formatPublicationEffectiveDate(effectiveDate);
  if (publication.uuid === activePublicationUuid) {
    return {
      label: effectiveDate ? `In effect since ${formattedDate}` : "In effect",
      shortLabel: "In effect",
      tone: "live",
    };
  }
  if (publication.is_superseded) {
    return {
      label: formattedDate
        ? `Superseded · Was effective ${formattedDate}`
        : "Superseded",
      shortLabel: "Superseded",
      tone: "previous",
    };
  }
  if (effectiveDate && effectiveDate > today) {
    return {
      label: `In effect ${formattedDate}`,
      shortLabel: "In effect",
      tone: "scheduled",
    };
  }
  return {
    label: effectiveDate ? `Previously live · Effective ${formattedDate}` : "Previously live",
    shortLabel: "Previous",
    tone: "previous",
  };
}

export function getNextScheduledPublication(
  publications = [],
  activePublicationUuid
) {
  return [...publications]
    .filter(
      (publication) =>
        getPublicationStatus(publication, activePublicationUuid).tone ===
        "scheduled"
    )
    .sort((first, second) => {
      const dateComparison = String(first.effective_date).localeCompare(
        String(second.effective_date)
      );
      return dateComparison || Number(second.version || 0) - Number(first.version || 0);
    })[0] || null;
}

export const publicationStatusToneClasses = {
  live: "bg-violet-100 text-violet-800",
  scheduled: "bg-amber-100 text-amber-800",
  previous: "bg-slate-100 text-slate-600",
  muted: "bg-slate-100 text-slate-500",
};
